export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

export type FileCheck = { ok: true } | { ok: false; message: string };

function headerBytes(file: Blob, count = 8): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, count));
  });
}

const startsWith = (b: Uint8Array, sig: number[]) => sig.every((v, i) => b[i] === v);

/**
 * Early, friendly check of a supporting document before anything is sent: size (10 MB), declared type, extension and the file's own first bytes (PDF, JPEG,
 * PNG). It is a convenience only; the server repeats every check and is the authority (a file that fails there is reported as not uploaded).
 */
export async function checkSupportingDocument(file: File): Promise<FileCheck> {
  if (file.size === 0) return { ok: false, message: 'That file is empty.' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, message: 'That file is larger than 10 MB.' };
  const ext = /\.([A-Za-z0-9]+)$/.exec(file.name)?.[1]?.toLowerCase() ?? '';
  const kind = ext === 'pdf' ? 'pdf' : ext === 'png' ? 'png' : ext === 'jpg' || ext === 'jpeg' ? 'jpg' : null;
  const typeOk = (ACCEPTED_TYPES as readonly string[]).includes(file.type);
  if (!kind || !typeOk) return { ok: false, message: 'Only PDF, JPG or PNG files can be attached.' };
  let bytes: Uint8Array;
  try {
    bytes = await headerBytes(file);
  } catch {
    return { ok: false, message: 'That file couldn’t be read. Choose it again.' };
  }
  const sigOk = kind === 'pdf' ? startsWith(bytes, [0x25, 0x50, 0x44, 0x46]) : kind === 'png' ? startsWith(bytes, [0x89, 0x50, 0x4e, 0x47]) : startsWith(bytes, [0xff, 0xd8, 0xff]);
  const typeMatches = (kind === 'pdf' && file.type === 'application/pdf') || (kind === 'png' && file.type === 'image/png') || (kind === 'jpg' && file.type === 'image/jpeg');
  return sigOk && typeMatches ? { ok: true } : { ok: false, message: 'That file doesn’t look like a valid PDF, JPG or PNG.' };
}
